/**
 * KLUB-264 U3 — where the "Todos" chip sits among the derived type chips.
 *
 * The type order lives in each type's `sort_order`. Only the "Todos" position
 * is new, and it is stored on the same scale as `sort_order`, not as a list
 * index: `position = p` means "Todos" sits right before the type whose
 * renumbered `sort_order` is `p`, and `N + 1` means it sits last. A member who
 * sees only some of the types still gets "Todos" in the right relative slot,
 * which an index would get wrong whenever chips are missing.
 *
 * null (no stored row, or position 1) means "Todos" first — the behaviour of
 * every tenant that never saved an order.
 */

/** The selection value the tabs use for the "Todos" chip. */
export const ALL_CHIP_VALUE = "__all__" as const;

/** Placeholder `placeAllChip` puts where "Todos" goes. A unique symbol, so it
 *  can never be confused with a type row. */
export const ALL_CHIP_MARKER: unique symbol = Symbol("klubora.allChip");

/**
 * Turns a raw stored value into a usable position. Only integers >= 2 are
 * kept: 1 is "first", which is the same as null, and anything that is not a
 * real integer (a string, a fraction, NaN, <= 0) falls back to null rather
 * than misplacing the chip. The upper bound is unknown here; `placeAllChip`
 * treats a position past the last type as "last".
 */
export function normalizeAllChipPosition(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isInteger(raw)) return null;
  return raw >= 2 ? raw : null;
}

/**
 * Inserts the "Todos" marker into `types`, which must already be sorted (as
 * `deriveTypesFromFeed` returns them). The marker goes right before the first
 * type whose `sort_order` is >= `position`, so "Todos" wins a tie; when no type
 * qualifies it goes last. A null position puts it first.
 */
export function placeAllChip<T extends { sort_order: number }>(
  types: readonly T[],
  position: number | null,
): Array<T | typeof ALL_CHIP_MARKER> {
  if (position === null) return [ALL_CHIP_MARKER, ...types];
  const at = types.findIndex((type) => type.sort_order >= position);
  const index = at === -1 ? types.length : at;
  return [...types.slice(0, index), ALL_CHIP_MARKER, ...types.slice(index)];
}

/**
 * The inverse, for the platform list: given the 0-based index of "Todos" in
 * the full list (types renumbered 1..N in list order, "Todos" excluded), the
 * position to store. Index 0 is "first", stored as null.
 */
export function allChipPositionFromIndex(index: number): number | null {
  return index <= 0 ? null : index + 1;
}
